import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Missionary Support Raising as a Couple: 7 Things to Get Right Together';
const DESCRIPTION =
    "A practical guide for married missionary couples raising support together — how to combine two networks without duplicating effort, tell one story instead of two, and divide the work so one spouse doesn't carry it alone.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover24.jpg',
            datePublished: '2026-07-27',
            dateModified: '2026-07-27',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/missionary-couple-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How do missionary couples combine two networks without asking the same people twice?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Build one shared contact list before either spouse starts reaching out individually, and mark who 'owns' the relationship with each contact. Sending two separate, uncoordinated asks to someone's in-laws or mutual friends reads as disorganized and can make a supporter feel targeted twice for the same trip.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should a missionary couple write one shared support letter or two individual ones?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "One shared letter written in a single, consistent voice works better than two separate letters or an alternating his-and-hers structure. Donors are supporting a couple's shared calling, not two individual campaigns, and a single coherent story is easier to follow and easier to remember.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How should missionary couples divide the work of support raising?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Split by task, not by contact list — one spouse leads on writing and follow-up emails, the other leads on scheduling calls and meetings, for example — rather than each spouse independently managing their own half of the network. Splitting by task keeps the story and the follow-up consistent no matter who a supporter is talking to.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What happens when one spouse is more comfortable asking for support than the other?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Let the more comfortable spouse lead the direct ask while the other contributes in ways that fit them better — writing, hosting, follow-up notes. Forcing the less comfortable spouse into asks they dread usually produces a worse ask than simply dividing the work around each person's actual strengths.",
                    },
                },
            ],
        },
    ],
};

export default function MissionaryCoupleSupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/missionary-couple-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-couple-support-raising-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover24.jpg" alt="A married couple reviewing paperwork together at a table, representing joint support raising" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Raising support as a couple sounds like it should be easier than doing it alone — two networks, two incomes, two people to share the work. In practice, it introduces its own set of problems: duplicated asks, mismatched comfort levels, and a story that can come out sounding like two campaigns stitched together instead of one. Here are the seven things worth getting right together before you start.</p>

                    <h2>1. Build one shared contact list before you start reaching out</h2>

                    <p>The single most common mistake is each spouse independently working their own list — which means a mutual friend, an in-law, or someone from your shared church gets two separate, uncoordinated asks. Before either of you sends a single message, build one combined list and assign an "owner" for each contact — usually whichever spouse has the closer relationship. It's a small step that prevents an avoidable, slightly awkward duplication that donors do notice.</p>

                    <h2>2. Write one letter, in one voice — not two</h2>

                    <p>It's tempting to alternate paragraphs — his section, her section — or send two separate letters covering the same trip. Resist it. Donors are supporting a shared calling, not two individual campaigns, and a single, coherent letter written in one consistent voice (even if you draft it together) is easier to read and easier to remember than a letter that switches narrators halfway through. If you're not sure how to structure it, our guide on <a href="/resources/how-to-write-a-missionary-support-letter">writing a missionary support letter that gets you funded</a> applies directly — just write it as "we," together, rather than as two people taking turns.</p>

                    <h2>3. Divide the work by task, not by contact list</h2>

                    <p>Splitting your network in half — "you handle your family, I'll handle mine" — sounds efficient but tends to produce two inconsistent follow-up styles and two different versions of the same story. Splitting by task instead keeps things consistent: one spouse leads on writing and email follow-up, the other leads on scheduling calls and in-person meetings, for example. Whoever a supporter talks to, they get the same story and the same tone.</p>

                    <h2>4. Let the more comfortable spouse lead the direct ask</h2>

                    <p>In almost every couple, one spouse is more naturally comfortable making a direct ask, and one is more comfortable with the behind-the-scenes work — writing, hosting, thank-you notes. Don't force an even 50/50 split on the parts that don't play to each person's strengths. Let the more comfortable spouse lead conversations and direct asks, and let the other contribute meaningfully elsewhere. A forced, uncomfortable ask from the reluctant spouse usually lands worse than a confident one from the spouse who's actually good at it.</p>

                    <h2>5. Budget as a household, not as two individual numbers</h2>

                    <p>It's easy to accidentally build two separate mental budgets — "my personal expenses" and "your personal expenses" — layered under a shared household number, which leads to double-counting or gaps. Build one combined household budget from the start, covering both of your actual costs together. Our guide on <a href="/resources/how-much-support-does-a-missionary-need">building a realistic monthly support budget</a> walks through the categories that most first-time budgets — couples included — miss.</p>

                    <h2>6. Agree on your update rhythm before you're both exhausted</h2>

                    <p>Newsletters and updates are one of the first things to slip when a couple assumes "the other one will probably send it." Decide up front who drafts, who edits, and who actually hits send — and build a simple, sustainable rhythm rather than an ambitious one neither of you keeps up once life on the field gets busy. Our <a href="/resources/free-missionary-newsletter-template">newsletter template</a> gives you a shared starting structure so you're not reinventing the format every month.</p>

                    <h2>7. Handle disagreement about the ask itself, privately, before donors see it</h2>

                    <p>Couples don't always agree on how directly to ask, what number to name, or how much of their story to share. Work through that disagreement privately, before a letter goes out or a meeting happens — not in front of a potential donor, and not through visibly mismatched follow-up messages later. A couple who's aligned on the approach, even if they compromised to get there, presents far more credibly than one whose asks quietly contradict each other.</p>

                    <p>Once you're funded and in a rhythm, a shared support page that shows both of you exactly which gifts have come in — one-time and recurring — removes a lot of the "wait, did you already thank them?" confusion that duplicated tracking creates. <a href="/">Start a page on Devotion Fund</a> to manage your support together from one place.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How do we combine our networks without asking the same people twice?</strong><br />
                    Build one shared contact list before either of you starts reaching out, and assign an owner per contact based on who has the closer relationship.</p>

                    <p><strong>Should we write one letter or two?</strong><br />
                    One shared letter in a single, consistent voice — donors are supporting your shared calling, not two separate campaigns.</p>

                    <p><strong>How should we divide the work?</strong><br />
                    Split by task — writing versus calls and meetings, for example — rather than splitting your contact list in half, so the story stays consistent no matter who a supporter talks to.</p>

                    <p><strong>What if one of us is much more comfortable asking than the other?</strong><br />
                    Let the more comfortable spouse lead direct asks, and let the other contribute where they're actually strong — writing, hosting, follow-up.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/single-missionary-support-raising">Single Missionary Support Raising: 9 Things That Are Different</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. This guide came out of watching how many couples' support-raising friction had nothing to do with donors at all, and everything to do with two people quietly working from two different plans.</p>
                    <p><small><em>Last updated: 2026-07-27.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/missionary-couple-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
